import { useState, useEffect } from 'react'
import { useAuthStore } from '../store/auth'
import { apiClient } from '../api/client'

interface Due {
  id: string
  amount: number
  paidAmount: number | null
  dueDate: string
  paidDate: string | null
  status: 'pending' | 'paid' | 'overdue' | 'cancelled'
  paymentMethod: string | null
  reminderCount: number
  reminderSentAt: string | null
  notes: string | null
  memberId: string
  memberName: string | null
  memberEmail: string
  feeId: string | null
  feeName: string | null
  feeType: string | null
}

interface OverdueDue extends Due {
  feeDescription: string | null
}

export default function MemberDuesPage() {
  const { activeOrg: currentOrganization } = useAuthStore()
  const [activeTab, setActiveTab] = useState<'all' | 'overdue' | 'pending'>('all')
  const [dues, setDues] = useState<Due[]>([])
  const [overdue, setOverdue] = useState<OverdueDue[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reminder modal state
  const [showReminderModal, setShowReminderModal] = useState(false)
  const [reminderTarget, setReminderTarget] = useState<Due | null>(null)
  const [reminderSubject, setReminderSubject] = useState('')
  const [reminderMessage, setReminderMessage] = useState('')
  const [sendingReminder, setSendingReminder] = useState(false)
  const [reminderResult, setReminderResult] = useState<{success: boolean; message: string} | null>(null)

  useEffect(() => {
    if (currentOrganization) {
      fetchDues()
      fetchOverdue()
    }
  }, [currentOrganization, activeTab])

  const fetchDues = async () => {
    if (!currentOrganization) return
    setLoading(true)
    try {
      const status = activeTab === 'all' ? 'all' : activeTab
      const data = await apiClient.get<{ dues: Due[] }>(
        `/members/dues?organization_id=${currentOrganization.id}&status=${status}`
      )
      if (data.dues) {
        setDues(data.dues)
      }
    } catch (e) {
      setError('Failed to load dues')
    } finally {
      setLoading(false)
    }
  }

  const fetchOverdue = async () => {
    if (!currentOrganization) return
    try {
      const data = await apiClient.get<{ overdue: OverdueDue[] }>(
        `/members/dues/overdue?organization_id=${currentOrganization.id}`
      )
      if (data.overdue) {
        setOverdue(data.overdue)
      }
    } catch (e) {
      console.error('Failed to load overdue dues')
    }
  }

  const formatCents = (cents: number | null) => {
    if (cents === null) return '0,00 €'
    return `${(cents / 100).toFixed(2).replace('.', ',')} €`
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('fi-FI')
  }

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',
    }
    const labels = {
      pending: 'Odottaa',
      paid: 'Maksettu',
      overdue: 'Myöhässä',
      cancelled: 'Peruutettu',
    }
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${styles[status as keyof typeof styles] || styles.pending}`}>
        {labels[status as keyof typeof labels] || status}
      </span>
    )
  }

  const handleSendReminder = (due: Due) => {
    setReminderTarget(due)
    setReminderSubject(`Maksumuistutus: ${due.feeName || 'Jäsenmaksu'}`)
    setReminderMessage(`Hei ${due.memberName || ''},\n\nTämä on muistutus maksamattomasta jäsenmaksustasi.\n\nMaksun tiedot:\n- Maksu: ${due.feeName || 'Jäsenmaksu'}\n- Summa: ${formatCents(due.amount - (due.paidAmount || 0))}\n- Eräpäivä: ${formatDate(due.dueDate)}\n\nMikäli olet jo maksanut, kiitos! Tämä muistutus on lähetetty automaattisesti.\n\nYstävällisin terveisin,\nYhdistyksen hallitus`)
    setShowReminderModal(true)
    setReminderResult(null)
  }

  const handleSendBulkReminders = () => {
    setReminderTarget(null)
    setReminderSubject('Maksumuistutus: Jäsenmaksu')
    setReminderMessage(`Hei,\n\nTämä on muistutus maksamattomasta jäsenmaksustasi.\n\nMikäli olet jo maksanut, kiitos! Tämä muistutus on lähetetty automaattisesti.\n\nYstävällisin terveisin,\nYhdistyksen hallitus`)
    setShowReminderModal(true)
    setReminderResult(null)
  }

  const submitReminder = async () => {
    if (!currentOrganization) return
    setSendingReminder(true)
    setReminderResult(null)

    try {
      if (reminderTarget) {
        // Single reminder
        await apiClient.post(`/members/dues/${reminderTarget.id}/remind`, {
          organization_id: currentOrganization.id,
          subject: reminderSubject,
          message: reminderMessage,
        })
        setReminderResult({
          success: true,
          message: `Muistutus lähetetty osoitteeseen ${reminderTarget.memberEmail}`,
        })
        // Refresh data
        fetchDues()
        fetchOverdue()
      } else {
        // Bulk reminder
        const result = await apiClient.post<{ sent_count: number }>('/members/dues/remind-all', {
          organization_id: currentOrganization.id,
          subject: reminderSubject,
          message: reminderMessage,
        })
        setReminderResult({
          success: true,
          message: `Lähetetty ${result.sent_count} muistutusta`,
        })
        // Refresh data
        fetchDues()
        fetchOverdue()
      }
    } catch (e: any) {
      setReminderResult({
        success: false,
        message: e?.error || 'Muistutuksen lähetys epäonnistui',
      })
    } finally {
      setSendingReminder(false)
    }
  }

  const displayedDues = activeTab === 'overdue' ? overdue : dues

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">💶 Jäsenmaksujen seuranta</h1>
          <p className="text-gray-600">Seuraa jäsenmaksuja ja lähetä maksumuistutuksia</p>
        </div>
        <div className="flex gap-2">
          {overdue.length > 0 && (
            <button
              onClick={handleSendBulkReminders}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
            >
              📧 Lähetä muistutukset kaikille ({overdue.length})
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Kaikki maksut</div>
          <div className="text-2xl font-bold text-gray-800">{dues.length}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Odottavat</div>
          <div className="text-2xl font-bold text-yellow-600">
            {dues.filter(d => d.status === 'pending').length}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Myöhässä</div>
          <div className="text-2xl font-bold text-red-600">
            {overdue.length}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">Maksettu</div>
          <div className="text-2xl font-bold text-green-600">
            {dues.filter(d => d.status === 'paid').length}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-gray-200 mb-6">
        {[
          { key: 'all', label: 'Kaikki', icon: '📋' },
          { key: 'pending', label: 'Odottavat', icon: '⏳' },
          { key: 'overdue', label: 'Myöhässä', icon: '⚠️' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as any)}
            className={`pb-3 px-4 border-b-2 transition ${
              activeTab === tab.key
                ? 'border-indigo-600 text-indigo-600 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.icon} {tab.label}
            {tab.key === 'overdue' && overdue.length > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full">
                {overdue.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">{error}</div>
      )}

      {/* Dues Table */}
      {loading ? (
        <div className="text-center py-8">Ladataan...</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Jäsen</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Maksu</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Summa</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Maksettu</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Eräpäivä</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Tila</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Muistutukset</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Toiminnot</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {displayedDues.map((due) => (
                <tr key={due.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{due.memberName || '-'}</div>
                    <div className="text-sm text-gray-500">{due.memberEmail}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{due.feeName || 'Jäsenmaksu'}</div>
                    <div className="text-sm text-gray-500">
                      {due.feeType === 'annual' ? 'Vuosimaksu' :
                       due.feeType === 'quarterly' ? 'Neljännesvuosimaksu' :
                       due.feeType === 'monthly' ? 'Kuukausimaksu' : 'Kertamaksu'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-medium">
                    {formatCents(due.amount)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {due.paidAmount ? (
                      <span className="text-green-600">{formatCents(due.paidAmount)}</span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={due.status === 'overdue' ? 'text-red-600 font-medium' : ''}>
                      {formatDate(due.dueDate)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {getStatusBadge(due.status)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {due.reminderCount > 0 ? (
                      <span className="text-sm">
                        {due.reminderCount}x
                        {due.reminderSentAt && (
                          <span className="text-gray-400 text-xs block">
                            {formatDate(due.reminderSentAt)}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {due.status !== 'paid' && due.status !== 'cancelled' && (
                      <button
                        onClick={() => handleSendReminder(due)}
                        className="text-orange-600 hover:text-orange-800 text-sm"
                      >
                        📧 Muistuta
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {displayedDues.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {activeTab === 'overdue'
                ? 'Ei myöhässä olevia maksuja 🎉'
                : activeTab === 'pending'
                ? 'Ei odottavia maksuja'
                : 'Ei maksuja näytettäväksi'}
            </div>
          )}
        </div>
      )}

      {/* Reminder Modal */}
      {showReminderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {reminderTarget ? 'Lähetä maksumuistutus' : 'Lähetä muistutukset kaikille'}
            </h2>

            {reminderTarget && (
              <div className="bg-gray-50 p-3 rounded mb-4">
                <div className="text-sm">
                  <strong>Vastaanottaja:</strong> {reminderTarget.memberName} ({reminderTarget.memberEmail})
                </div>
                <div className="text-sm">
                  <strong>Maksu:</strong> {reminderTarget.feeName || 'Jäsenmaksu'} - {formatCents(reminderTarget.amount - (reminderTarget.paidAmount || 0))}
                </div>
              </div>
            )}

            {!reminderTarget && overdue.length > 0 && (
              <div className="bg-orange-50 p-3 rounded mb-4">
                <div className="text-sm text-orange-800">
                  <strong>Huomio:</strong> Muistutus lähetetään {overdue.length} jäsenelle, joilla on myöhässä olevia maksuja.
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Aihe</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={reminderSubject}
                  onChange={(e) => setReminderSubject(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Viesti</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                  rows={10}
                  value={reminderMessage}
                  onChange={(e) => setReminderMessage(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Voit muokata viestiä. Jäsenen nimi ja maksutiedot lisätään automaattisesti.
                </p>
              </div>

              {reminderResult && (
                <div className={`p-3 rounded ${reminderResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {reminderResult.message}
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowReminderModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Peruuta
                </button>
                <button
                  onClick={submitReminder}
                  disabled={sendingReminder || !reminderSubject || !reminderMessage}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
                >
                  {sendingReminder ? 'Lähetetään...' : 'Lähetä muistutus'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
